import { useEffect, useState, useCallback } from 'react';
import { webSocketService, type WebSocketEventHandler, type NewContactMessageHandler } from '../services/websocket/websocket.service';

export interface UseWebSocketReturn {
  isConnected: boolean;
  connectionStatus: 'connected' | 'connecting' | 'disconnected' | 'error';
  connect: () => void;
  disconnect: () => void;
  addEventListener: (eventType: 'permission_change', handler: WebSocketEventHandler) => void;
  removeEventListener: (eventType: 'permission_change', handler: WebSocketEventHandler) => void;
  addContactMessageListener: (handler: NewContactMessageHandler) => void;
  removeContactMessageListener: (handler: NewContactMessageHandler) => void;
}

export const useWebSocket = (): UseWebSocketReturn => {
  const [isConnected, setIsConnected] = useState(false);
  const [connectionStatus, setConnectionStatus] = useState<'connected' | 'connecting' | 'disconnected' | 'error'>('disconnected');

  // Función para conectar WebSocket
  const connect = useCallback(() => {
    const token = localStorage.getItem('accessToken');
    if (token) {
      webSocketService.connect(token)
        .catch(error => {
          console.error('Failed to connect WebSocket:', error);
        });
    }
  }, []);

  // Función para desconectar WebSocket
  const disconnect = useCallback(() => {
    webSocketService.disconnect();
  }, []);

  // Funciones para manejo de eventos
  const addEventListener = useCallback((eventType: 'permission_change', handler: WebSocketEventHandler) => {
    webSocketService.addEventListener(eventType, handler);
  }, []);

  const removeEventListener = useCallback((eventType: 'permission_change', handler: WebSocketEventHandler) => {
    webSocketService.removeEventListener(eventType, handler);
  }, []);

  const addContactMessageListener = useCallback((handler: NewContactMessageHandler) => {
    webSocketService.addContactMessageListener(handler);
  }, []);

  const removeContactMessageListener = useCallback((handler: NewContactMessageHandler) => {
    webSocketService.removeContactMessageListener(handler);
  }, []);

  // Efecto para manejar el estado de conexión
  useEffect(() => {
    const handleStatusChange = (status: typeof connectionStatus) => {
      setConnectionStatus(status);
      setIsConnected(status === 'connected');
    };

    // Agregar listener para cambios de estado
    webSocketService.addStatusListener(handleStatusChange);

    // Auto-conectar si hay token disponible
    const token = localStorage.getItem('accessToken');
    if (token && webSocketService.getConnectionStatus() === 'disconnected') {
      connect();
    }

    // Cleanup
    return () => {
      webSocketService.removeStatusListener(handleStatusChange);
    };
  }, [connect]);

  // Efecto para reconectar cuando cambie el token
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'accessToken') {
        if (e.newValue) {
          // Nuevo token disponible, reconectar
          connect();
        } else {
          // Token removido, desconectar
          disconnect();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [connect, disconnect]);

  return {
    isConnected,
    connectionStatus,
    connect,
    disconnect,
    addEventListener,
    removeEventListener,
    addContactMessageListener,
    removeContactMessageListener,
  };
};